import type { AppConfig, ExtensionSettings, MarkData, WritebackPayload } from '../config/types';
import type { TableauEnvironment } from '../tableau/init';
import { findWorksheet, subscribeToSelectionChanges, getSelectedMarks } from '../tableau/worksheet';
import { formatInlineMarkSummary, getFieldValue, mapSelectedMark } from '../tableau/mapping';
import { submitWritebackToApi, WritebackApiError } from '../api/writebackApi';
import { sendDebugLog } from '../dev/debug';
import { getAvailableWorksheets } from '../tableau/settings';
import {
  createForm,
  updateFormWithMark,
  getFormState,
  resetForm,
  updateSubmitEnabled,
  bindFormEvents,
  type FormElements,
} from './form';
import {
  createStatusElement,
  showLoading,
  showSuccess,
  showError,
  clearStatus,
} from './status';

export interface AppState {
  config: AppConfig;
  settings: ExtensionSettings;
  environment: TableauEnvironment;
  currentMark: MarkData | null;
  isSubmitting: boolean;
}

export function createApp(
  config: AppConfig,
  settings: ExtensionSettings,
  environment: TableauEnvironment
): void {
  const root = document.getElementById('app');
  if (!root) {
    console.error('App root element not found');
    return;
  }

  const state: AppState = {
    config,
    settings,
    environment,
    currentMark: null,
    isSubmitting: false,
  };

  const formElements = createForm(settings.dropdownOptions);
  const statusElement = createStatusElement();

  root.appendChild(formElements.container);
  root.appendChild(statusElement);

  const availableWorksheets = getAvailableWorksheets();
  void sendDebugLog('writeback-init', {
    dashboardName: state.environment.dashboardName,
    isMock: state.environment.isMock,
    configuredWorksheet: settings.worksheetName,
    availableWorksheets,
    fieldMapping: settings.fieldMapping,
  });

  bindFormEvents(formElements, () => {
    const formState = getFormState(formElements);
    updateSubmitEnabled(
      formElements,
      state.currentMark !== null,
      formState.isValid,
      state.isSubmitting
    );
  });

  formElements.submitButton.addEventListener('click', () => {
    void handleSubmit(state, formElements, statusElement);
  });

  if (!settings.worksheetName) {
    void sendDebugLog('worksheet-config-missing', {
      dashboardName: state.environment.dashboardName,
      availableWorksheets,
    });
    showError(statusElement, 'Configuration error: No worksheet configured');
    window.setTimeout(() => clearStatus(statusElement), 5000);
    return;
  }

  const worksheet = findWorksheet(settings.worksheetName);
  if (!worksheet) {
    void sendDebugLog('worksheet-not-found', {
      configuredWorksheet: settings.worksheetName,
      availableWorksheets,
      dashboardName: state.environment.dashboardName,
    });
    showError(
      statusElement,
      `Worksheet "${settings.worksheetName}" not found. Available: ${availableWorksheets.join(', ')}`
    );
    window.setTimeout(() => clearStatus(statusElement), 5000);
    return;
  }

  void sendDebugLog('worksheet-bound', {
    configuredWorksheet: settings.worksheetName,
    dashboardName: state.environment.dashboardName,
  });

  subscribeToSelectionChanges(worksheet, (marksTable) => {
    state.currentMark = marksTable ? mapSelectedMark(marksTable, settings.fieldMapping) : null;

    void logSelectedMark(state.currentMark, {
      source: 'selection-event',
      worksheetName: settings.worksheetName,
      rowCount: marksTable?.totalRowCount ?? 0,
    });

    updateFormWithMark(formElements, state.currentMark);

    const formState = getFormState(formElements);
    updateSubmitEnabled(
      formElements,
      state.currentMark !== null,
      formState.isValid,
      state.isSubmitting
    );
  });

  void getSelectedMarks(worksheet).then((marksTable) => {
    void sendDebugLog('initial-selection-fetch', {
      worksheetName: settings.worksheetName,
      hasSelection: Boolean(marksTable),
      rowCount: marksTable?.totalRowCount ?? 0,
    });

    if (!marksTable) {
      return;
    }

    state.currentMark = mapSelectedMark(marksTable, settings.fieldMapping);
    void logSelectedMark(state.currentMark, {
      source: 'initial-fetch',
      worksheetName: settings.worksheetName,
      rowCount: marksTable.totalRowCount,
    });
    updateFormWithMark(formElements, state.currentMark);

    const formState = getFormState(formElements);
    updateSubmitEnabled(
      formElements,
      state.currentMark !== null,
      formState.isValid,
      state.isSubmitting
    );
  });
}

async function handleSubmit(
  state: AppState,
  formElements: FormElements,
  statusElement: HTMLElement
): Promise<void> {
  if (state.isSubmitting) {
    return;
  }

  if (!state.currentMark) {
    showError(statusElement, 'No row selected');
    window.setTimeout(() => clearStatus(statusElement), 3000);
    return;
  }

  const formState = getFormState(formElements);
  if (!formState.isValid) {
    showError(statusElement, 'Please select a dismissal reason');
    window.setTimeout(() => clearStatus(statusElement), 3000);
    return;
  }

  state.isSubmitting = true;
  formElements.submitButton.classList.add('submitting');
  updateSubmitEnabled(formElements, true, true, true);
  showLoading(statusElement);

  try {
    const payload = buildPayload(state, formState.dropdownValue);
    await submitWritebackToApi(state.config.writebackApiUrl, payload);

    showSuccess(statusElement, 'Dismissal saved');
    resetForm(formElements);
    window.setTimeout(() => clearStatus(statusElement), 3000);
  } catch (err) {
    const message = getErrorMessage(err);
    showError(statusElement, message);
    console.error('Submit error:', err);
    window.setTimeout(() => clearStatus(statusElement), 5000);
  } finally {
    state.isSubmitting = false;
    formElements.submitButton.classList.remove('submitting');
    const formState = getFormState(formElements);
    updateSubmitEnabled(
      formElements,
      state.currentMark !== null,
      formState.isValid,
      false
    );
  }
}

function buildPayload(
  state: AppState,
  dropdownValue: string
): WritebackPayload {
  return {
    environment: state.config.appEnv,
    submitted_at_utc: new Date().toISOString(),
    alert_id: resolveAlertId(state),
    tableau_user_id: state.environment.userId,
    dismissal_reason: dropdownValue,
    tableau_workbook_name: state.environment.workbookName,
    tableau_dashboard_name: state.settings.dashboardLabel ?? state.environment.dashboardName,
    tableau_worksheet_name: state.settings.worksheetName || null,
    source_record_json: state.currentMark?.fields ?? {},
    processing_status: 'pending',
    processing_notes: null,
  };
}

function resolveAlertId(state: AppState): string {
  const markData = state.currentMark;
  if (!markData) {
    throw new Error('No selected row');
  }

  const mappedAlertId = getFieldValue(markData, 'alert_id');
  if (mappedAlertId !== null && mappedAlertId !== undefined && mappedAlertId !== '') {
    return String(mappedAlertId);
  }

  if (markData.key) {
    return markData.key;
  }

  throw new Error('Selected row is missing "alert_id"');
}

async function logSelectedMark(
  markData: MarkData | null,
  metadata: Record<string, unknown>
): Promise<void> {
  const summary = markData ? formatInlineMarkSummary(markData) : 'No selection';

  await sendDebugLog('selected-row', {
    ...metadata,
    summary,
    selection: markData,
  });
}

function getErrorMessage(err: unknown): string {
  if (err instanceof WritebackApiError) {
    if (err.statusCode === 401 || err.statusCode === 403) {
      return 'Writeback API authentication failed';
    }

    return `Writeback API error: ${err.message}`;
  }

  if (err instanceof Error) {
    return err.message;
  }

  return 'An unexpected error occurred';
}
